import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, Loader2, ArrowRight } from 'lucide-react';

const LoginPage = () => {
  const { language } = useLanguage();
  const { login, error: authError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const content = {
    en: {
      title: "Welcome Back",
      subtitle: "Sign in to access your learning portal",
      studentTitle: "Student Portal",
      studentSubtitle: "Sign in to access your courses and lessons",
      email: "Email Address",
      password: "Password",
      signIn: "Sign In",
      signInWithGoogle: "Continue with Google",
      or: "or",
      noAccount: "Don't have an account?",
      register: "Register here",
      forgotPassword: "Forgot password?",
      error: "Invalid email or password",
      backToHome: "Back to Home"
    },
    ar: {
      title: "مرحباً بعودتك",
      subtitle: "سجل الدخول للوصول إلى بوابة التعلم الخاصة بك",
      studentTitle: "بوابة الطالب",
      studentSubtitle: "سجل الدخول للوصول إلى دوراتك ودروسك",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      signIn: "تسجيل الدخول",
      signInWithGoogle: "المتابعة مع Google",
      or: "أو",
      noAccount: "ليس لديك حساب؟",
      register: "سجل هنا",
      forgotPassword: "نسيت كلمة المرور؟",
      error: "البريد الإلكتروني أو كلمة المرور غير صحيحة",
      backToHome: "العودة للرئيسية"
    }
  };

  const t = content[language];
  const isStudentLogin = role === 'student';

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    const redirectUrl = window.location.origin + '/student';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      // Redirect based on role
      switch (user.role) {
        case 'admin':
          navigate('/admin');
          break;
        case 'teacher':
          navigate('/teacher');
          break;
        case 'moderator':
          navigate('/moderator');
          break;
        case 'student':
          navigate('/student');
          break;
        default:
          navigate('/');
      }
    } catch (err) {
      setError(err.message || t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-md">
        {/* Logo and Back Link */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-6">
            <ArrowRight className={`w-4 h-4 ${language === 'ar' ? 'ml-2 rotate-180' : 'mr-2 rotate-180'}`} />
            {t.backToHome}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isStudentLogin ? t.studentTitle : t.title}
          </h1>
          <p className="text-gray-600">
            {isStudentLogin ? t.studentSubtitle : t.subtitle}
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Google Login Button - Only for students */}
          {isStudentLogin && (
            <>
              <Button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                variant="outline"
                className="w-full h-12 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-medium text-base flex items-center justify-center gap-3"
                data-testid="google-login-btn"
              >
                {googleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t.signInWithGoogle}
                  </>
                )}
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">{t.or}</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {(error || authError) && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {error || authError}
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                {t.email}
              </Label>
              <div className="relative">
                <Mail className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`${language === 'ar' ? 'pr-10 text-right' : 'pl-10'} h-12`}
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">
                {t.password}
              </Label>
              <div className="relative">
                <Lock className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${language === 'ar' ? 'pr-10 text-right' : 'pl-10'} h-12`}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${language === 'ar' ? 'left-3' : 'right-3'}`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t.signIn
              )}
            </Button>

            {/* Forgot Password Link */}
            <div className="text-center mt-4">
              <Link 
                to="/forgot-password" 
                className="text-sm text-gray-500 hover:text-teal-600 transition-colors"
                data-testid="forgot-password-link"
              >
                {t.forgotPassword}
              </Link>
            </div>
          </form>

          {/* Register Link - Only for students */}
          {isStudentLogin && (
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                {t.noAccount}{' '}
                <Link to="/register" className="text-teal-600 hover:text-teal-700 font-semibold">
                  {t.register}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
